import { z } from 'zod';

export const DYNAMIC_FIELD_KEYS = [
  'propertyOnBoardLimit',
  'transitLimit',
  'atmLimit',
  'compusafeLimit',
  'moneyProcessingLimit',
  'storageLocations',
  'situation',
  'sanctionedCountryTouchpoint',
  'additionalComments',
] as const;

export type DynamicFieldKey = (typeof DYNAMIC_FIELD_KEYS)[number];

const requiredString = (message: string) => z.string({ error: message }).trim().min(1, message);

const policyYear = (requiredMessage: string, invalidYearMessage: string) =>
  requiredString(requiredMessage).regex(/^\d{4}$/, invalidYearMessage);

const amount = (requiredMessage: string, invalidAmountMessage: string) =>
  z.number({ error: requiredMessage }).min(0, invalidAmountMessage);

const optionalString = () => z.string();

const requiredDate = (message: string) =>
  z
    .date({ error: message })
    .nullable()
    .refine((value): boolean => value !== null, { error: message });

export interface SchemaOptions {
  requiredMessage: string;
  invalidYearMessage: string;
  invalidAmountMessage: string;
  dynamicFieldKeys: DynamicFieldKey[];
}

export const getSchema = ({
  requiredMessage,
  invalidYearMessage,
  invalidAmountMessage,
  dynamicFieldKeys,
}: SchemaOptions) => {
  const has = (key: DynamicFieldKey) => dynamicFieldKeys.includes(key);

  const conditionalString = (key: DynamicFieldKey) =>
    has(key) ? requiredString(requiredMessage) : optionalString();

  const conditionalAmount = (key: DynamicFieldKey) =>
    has(key) ? amount(requiredMessage, invalidAmountMessage) : z.number().optional();

  const storageLocation = z.object({
    id: z.string().min(1),
    storageLocation: requiredString(requiredMessage),
    limitStorage: amount(requiredMessage, invalidAmountMessage),
  });

  return z.object({
    certificateIssuedTo: requiredString(requiredMessage),
    customerAddress: requiredString(requiredMessage),
    countryCode: requiredString(requiredMessage),
    policyYear: policyYear(requiredMessage, invalidYearMessage),
    certificateTemplateId: requiredString(requiredMessage),
    effectiveDate: requiredDate(requiredMessage),
    currency: requiredString(requiredMessage),
    policyExpirationDate: requiredDate(requiredMessage),
    policyNumber: requiredString(requiredMessage),
    contractingEntity: requiredString(requiredMessage),
    propertyOnBoardLimit: conditionalAmount('propertyOnBoardLimit'),
    transitLimit: conditionalAmount('transitLimit'),
    atmLimit: conditionalAmount('atmLimit'),
    compusafeLimit: conditionalAmount('compusafeLimit'),
    moneyProcessingLimit: conditionalAmount('moneyProcessingLimit'),
    storageLocations: has('storageLocations')
      ? z.array(storageLocation).min(1, requiredMessage)
      : z.array(storageLocation),
    situation: conditionalString('situation'),
    sanctionedCountryTouchpoint: z.boolean(),
    additionalComments: optionalString(),
  });
};

export type FormValues = z.infer<ReturnType<typeof getSchema>>;
